import "../compose/ComposeEmail.css";

import React, { FC, useRef, useState } from "react";
import { HiOutlineStar, HiStar } from "react-icons/hi2";
import {
  LuAlertCircle,
  LuArrowLeft,
  LuArrowRight,
  LuBriefcase,
  LuCheck,
  LuChevronDown,
  LuDownload,
  LuFolderInput,
  LuForward,
  LuLink2Off,
  LuMail,
  LuPlus,
  LuPrinter,
  LuRefreshCw,
  LuReply,
  LuReplyAll,
  LuTrash2,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { Email } from "../../../factories/emails/models/emails.models";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  AiEmailInsights,
  AiEmailStepKind,
  AiEmailTriageItem,
} from "../../../slices/AiSlice";
import { useGetCustomersByEmailQuery } from "../../../slices/ContactsSlice";
import { useGetWorkQuery } from "../../../slices/WorkSlice";
import { navigateToContact } from "../../contacts/utils/navigateToContacts";
import { initialsOf } from "../compose/composeEmail.utils";
import { SparkleBadge, SparkleIcon } from "../compose/SparkleIcon";
import { avatarColor } from "./MailList";
import { displayName, fullDate, INTENTS, senderOf } from "./mailPage.utils";

type Props = {
  email: Email | null;
  outgoing: boolean;
  linkedWorkId?: string;
  tag?: AiEmailTriageItem;
  aiOn: boolean;
  insight?: AiEmailInsights;
  reading: boolean;
  readError: string | null;
  autoRead: boolean;
  onRead: (force?: boolean) => void;
  onReply: (opts?: { text?: string; label?: string; prompt?: string }) => void;
  onReplyAll: () => void;
  onForward: () => void;
  onStar: () => void;
  onDelete: () => void;
  onAddToWork: () => void;
  onCreateWork: () => void;
  onUnlinkWork: () => void;
  onOpenAttachment: (
    attachment: NonNullable<Email["attachments"]>[number],
  ) => void;
  /** Documents: "Save to client files" for one attachment. */
  onSaveAttachment: (
    attachment: NonNullable<Email["attachments"]>[number],
  ) => void;
  onCompose: () => void;
  /** Reading pane "Off": the email fills the page; Back returns to the list. */
  onBack?: () => void;
  backLabel?: string;
};

const STEP_CTA: Record<AiEmailStepKind, string> = {
  reply: "Reply",
  create_work: "Create work",
  add_to_work: "Add to work",
  open_client: "Open client",
  open_work: "Open work",
  note: "",
};

/** Right column: the open email, its AI summary and the reply dock. */
export const MailReader: FC<Props> = (p) => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";
  const email = p.email;
  const sender = email ? senderOf(email) : undefined;
  const { data: clients } = useGetCustomersByEmailQuery(
    { email: sender?.email || "", orgId },
    { skip: !sender?.email || !orgId || p.outgoing },
  );
  const client = clients?.[0];
  const { data: work } = useGetWorkQuery(
    { workId: p.linkedWorkId || "" },
    { skip: !p.linkedWorkId },
  );
  const [sumOpen, setSumOpen] = useState(true);
  const [done, setDone] = useState<Record<string, boolean>>({});
  const bodyRef = useRef<HTMLDivElement>(null);

  if (!email) {
    return (
      <section
        className={
          "flex min-w-0 flex-1 flex-col items-center justify-center gap-[10px] rounded-[12px] border border-[#E5E7EB] bg-white p-6 text-center"
        }>
        <span
          className={
            "inline-flex h-14 w-14 items-center justify-center rounded-[14px] bg-[#F1EEFF] text-[#6B4EFF]"
          }>
          <LuMail size={26} />
        </span>
        <span className={"text-[15px] font-bold text-[#1F2433]"}>
          Select an email to read
        </span>
        <button
          type={"button"}
          onClick={p.onCompose}
          className={"text-[13.5px] font-bold text-[#6B4EFF]"}>
          or write a new one
        </button>
      </section>
    );
  }

  const tag = p.tag
    ? INTENTS[p.tag.intent]
    : p.insight
    ? INTENTS[p.insight.intent]
    : null;
  const files = (email.attachments || []).filter((a) => !a.is_inline);
  const labelFor = (name: string) =>
    p.insight?.attachmentLabels.find(
      (l) => l.name.toLowerCase() === name.toLowerCase(),
    )?.label;
  const openClient = () =>
    client &&
    navigate(
      navigateToContact({
        type: client.contactId ? "contact" : "organization",
        customerId: client.id || "",
        id: client.businessContactId || "",
      }),
    );

  const runStep = (kind: AiEmailStepKind, key: string) => {
    if (kind === "reply")
      p.onReply(
        p.insight?.replies[0] ? { text: p.insight.replies[0].text } : undefined,
      );
    if (kind === "create_work") p.onCreateWork();
    if (kind === "add_to_work") p.onAddToWork();
    if (kind === "open_client") openClient();
    if (kind === "open_work" && p.linkedWorkId)
      navigate(`/work/${p.linkedWorkId}`);
    setDone((d) => ({ ...d, [key]: true }));
  };
  const stepAvailable = (kind: AiEmailStepKind) =>
    kind === "note" ||
    kind === "reply" ||
    kind === "create_work" ||
    (kind === "add_to_work" && !p.linkedWorkId) ||
    (kind === "open_client" && !!client) ||
    (kind === "open_work" && !!p.linkedWorkId);

  const print = () => {
    const w = window.open("", "_blank", "width=800,height=600");
    if (!w || !bodyRef.current) return;
    w.document.write(
      `<html><head><title>${
        email.subject || "Email"
      }</title><style>body{font-family:Arial,sans-serif;padding:20px}</style></head><body><h2>${
        email.subject || ""
      }</h2><p>${displayName(sender)} &lt;${
        sender?.email || ""
      }&gt;<br/>${fullDate(email.date)}</p>${
        bodyRef.current.innerHTML
      }</body></html>`,
    );
    w.document.close();
    w.focus();
    w.onload = () => {
      w.print();
      w.close();
    };
  };

  const iconBtn = (
    label: string,
    onClick: () => void,
    icon: React.ReactNode,
    danger = false,
  ) => (
    <button
      type={"button"}
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`inline-flex h-[34px] w-[34px] items-center justify-center rounded-[8px] text-[#5B6678] ${
        danger
          ? "hover:bg-[#FEECEC] hover:text-[#E5484D]"
          : "hover:bg-[#F4F2FF] hover:text-[#6B4EFF]"
      }`}>
      {icon}
    </button>
  );

  const replies = !p.outgoing ? p.insight?.replies || [] : [];
  const noReply =
    !p.outgoing && p.insight?.intent === "fyi" && replies.length === 0;

  return (
    <section
      aria-label={"Email"}
      className={
        "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-white"
      }>
      {p.onBack && (
        <div className={"flex-none px-[14px] pt-3"}>
          <button
            type={"button"}
            onClick={p.onBack}
            className={
              "inline-flex h-8 items-center gap-[6px] rounded-[8px] px-[10px] text-[13px] font-bold text-[#5B6678] hover:bg-[#F4F2FF] hover:text-[#6B4EFF]"
            }>
            <LuArrowLeft size={16} />
            {p.backLabel || "Back"}
          </button>
        </div>
      )}
      <div
        className={"flex flex-none items-start gap-4 px-[22px] pb-3 pt-[18px]"}>
        <div className={"flex min-w-0 flex-1 flex-col gap-[7px]"}>
          {tag && (
            <span
              className={
                "inline-flex h-[22px] items-center self-start rounded-[6px] px-2 text-[11.5px] font-bold"
              }
              style={{ background: tag.bg, color: tag.color }}>
              {tag.label}
            </span>
          )}
          <h2
            className={
              "m-0 text-[19px] font-extrabold leading-[1.35] tracking-[-0.2px] text-[#1F2433]"
            }>
            {email.subject || "(no subject)"}
          </h2>
        </div>
        <div className={"flex flex-none items-center gap-[2px]"}>
          {iconBtn("Reply", () => p.onReply(), <LuReply size={18} />)}
          {iconBtn("Reply all", p.onReplyAll, <LuReplyAll size={18} />)}
          {iconBtn("Forward", p.onForward, <LuForward size={18} />)}
          <span className={"mx-[6px] h-5 w-px bg-[#E5E7EB]"} />
          <button
            type={"button"}
            title={email.starred ? "Unstar" : "Star"}
            aria-label={email.starred ? "Unstar" : "Star"}
            onClick={p.onStar}
            className={
              "inline-flex h-[34px] w-[34px] items-center justify-center rounded-[8px] hover:bg-[#FFF8E1]"
            }
            style={{ color: email.starred ? "#F5B400" : "#5B6678" }}>
            {email.starred ? <HiStar size={19} /> : <HiOutlineStar size={19} />}
          </button>
          {iconBtn("Print", print, <LuPrinter size={17} />)}
          {iconBtn("Delete", p.onDelete, <LuTrash2 size={17} />, true)}
        </div>
      </div>

      <div className={"min-h-0 flex-1 overflow-y-auto px-[22px] pb-[22px]"}>
        {/* Linked to */}
        <div
          className={
            "flex flex-wrap items-center gap-[10px] rounded-[10px] border border-[#EEF0F3] bg-[#F7F8FA] px-3 py-[9px]"
          }>
          <span className={"text-[12.5px] font-bold text-[#8A90A2]"}>
            Linked to
          </span>
          {client ? (
            <button
              type={"button"}
              onClick={openClient}
              className={
                "inline-flex h-7 items-center gap-[7px] rounded-[14px] border border-[#E5E7EB] bg-white pl-[3px] pr-[10px] text-[13px] font-bold text-[#1F2433] hover:border-[#6B4EFF]"
              }>
              <span
                className={
                  "inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-[9px] font-bold text-white"
                }
                style={{
                  background: client.businessContactId ? "#6B4EFF" : "#1B3E9F",
                }}>
                {initialsOf(client.name)}
              </span>
              {client.name}
            </button>
          ) : (
            !p.outgoing && (
              <span className={"text-[13px] text-[#8A90A2]"}>
                No matching client
              </span>
            )
          )}
          {p.linkedWorkId && (
            <button
              type={"button"}
              onClick={() => navigate(`/work/${p.linkedWorkId}`)}
              className={
                "inline-flex h-7 items-center gap-[6px] rounded-[7px] bg-[#F1EEFF] px-[10px] text-[13px] font-bold text-[#6B4EFF]"
              }>
              <LuBriefcase size={13} />
              {work?.name || "Linked work item"}
            </button>
          )}
          <span className={"flex-1"} />
          {p.linkedWorkId ? (
            <button
              type={"button"}
              onClick={p.onUnlinkWork}
              className={
                "inline-flex h-[30px] items-center gap-[6px] rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-[11px] text-[12.5px] font-bold text-[#3A4256] hover:border-[#E5484D] hover:text-[#E5484D]"
              }>
              <LuLink2Off size={13} />
              Unlink work
            </button>
          ) : (
            <>
              <button
                type={"button"}
                onClick={p.onAddToWork}
                className={
                  "inline-flex h-[30px] items-center gap-[6px] rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-[11px] text-[12.5px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
                }>
                Add to work
              </button>
              <button
                type={"button"}
                onClick={p.onCreateWork}
                className={
                  "inline-flex h-[30px] items-center gap-[6px] rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-[11px] text-[12.5px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
                }>
                <LuPlus size={13} />
                Create work
              </button>
            </>
          )}
        </div>

        {/* AI summary */}
        {p.aiOn && !p.outgoing && (
          <div
            className={
              "mt-3 overflow-hidden rounded-[12px] border border-[#E4DCFF]"
            }
            style={{
              background: "linear-gradient(135deg,#F6F3FF 0%,#FCFAFF 100%)",
            }}>
            <button
              type={"button"}
              onClick={() => setSumOpen((v) => !v)}
              className={
                "flex w-full items-center gap-[9px] px-[14px] py-[11px] text-left"
              }>
              <SparkleBadge size={24} />
              <span className={"text-[13.5px] font-extrabold text-[#2A1F66]"}>
                AI summary
              </span>
              <span className={"text-[12px] text-[#9A90C8]"}>
                {p.reading
                  ? "Reading this email…"
                  : p.insight
                  ? "AI can make mistakes. Check before acting."
                  : ""}
              </span>
              <span className={"flex-1"} />
              <LuChevronDown
                size={16}
                className={"text-[#9A90C8] transition-transform"}
                style={{ transform: sumOpen ? "none" : "rotate(-90deg)" }}
              />
            </button>
            {sumOpen && (
              <div className={"pb-[14px] pl-[47px] pr-[14px]"}>
                {p.reading && (
                  <div className={"flex flex-col gap-2"}>
                    <span
                      className={"compose-ai-shimmer h-3 w-11/12 rounded"}
                    />
                    <span className={"compose-ai-shimmer h-3 w-3/4 rounded"} />
                    <span className={"compose-ai-shimmer h-3 w-1/2 rounded"} />
                  </div>
                )}
                {!p.reading && p.readError && (
                  <div
                    className={
                      "flex items-center gap-3 text-[13px] text-[#8A4B10]"
                    }>
                    <LuAlertCircle size={15} className={"flex-none"} />
                    <span className={"flex-1"}>{p.readError}</span>
                    <button
                      type={"button"}
                      onClick={() => p.onRead(true)}
                      className={
                        "inline-flex items-center gap-[5px] font-bold text-[#6B4EFF]"
                      }>
                      <LuRefreshCw size={13} />
                      Try again
                    </button>
                  </div>
                )}
                {!p.reading && !p.readError && !p.insight && (
                  <div
                    className={
                      "flex items-center gap-3 text-[13px] text-[#6B7185]"
                    }>
                    <span className={"flex-1"}>
                      {p.autoRead
                        ? "Getting the summary ready…"
                        : "Summaries are paused to save today's AI uses for writing."}
                    </span>
                    <button
                      type={"button"}
                      onClick={() => p.onRead(true)}
                      className={
                        "inline-flex h-[30px] items-center gap-[6px] rounded-[8px] bg-[#6B4EFF] px-3 text-[12.5px] font-bold text-white"
                      }>
                      <SparkleIcon size={13} />
                      Summarize
                    </button>
                  </div>
                )}
                {!p.reading && p.insight && (
                  <>
                    <div className={"text-[14px] leading-[1.6] text-[#2B3345]"}>
                      {p.insight.summary}
                    </div>
                    {p.insight.points.length > 0 && (
                      <div className={"mt-2 flex flex-col gap-1"}>
                        {p.insight.points.map((pt) => (
                          <div
                            key={pt}
                            className={
                              "flex items-start gap-2 text-[13px] leading-[1.5] text-[#4A5268]"
                            }>
                            <LuAlertCircle
                              size={13}
                              className={"mt-[3px] flex-none text-[#D97706]"}
                            />
                            <span>{pt}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {p.insight.nextSteps.filter((s) => stepAvailable(s.kind))
                      .length > 0 && (
                      <>
                        <div
                          className={
                            "mb-[7px] mt-[14px] text-[11.5px] font-extrabold uppercase tracking-[0.4px] text-[#9A90C8]"
                          }>
                          Suggested next steps
                        </div>
                        <div className={"flex flex-col gap-[6px]"}>
                          {p.insight.nextSteps
                            .filter((s) => stepAvailable(s.kind))
                            .map((s) => {
                              const key = `${email.id}:${s.label}`;
                              const isDone = !!done[key];
                              return (
                                <div
                                  key={key}
                                  className={
                                    "flex items-center gap-[11px] rounded-[9px] border bg-white py-2 pl-[11px] pr-2"
                                  }
                                  style={{
                                    borderColor: isDone ? "#D7F0E0" : "#ECE7FF",
                                  }}>
                                  <span
                                    className={
                                      "inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full"
                                    }
                                    style={{
                                      background: isDone
                                        ? "#E7F7EE"
                                        : "#F1EEFF",
                                      color: isDone ? "#15803D" : "#6B4EFF",
                                    }}>
                                    {isDone ? (
                                      <LuCheck size={12} />
                                    ) : (
                                      <LuArrowRight size={12} />
                                    )}
                                  </span>
                                  <span
                                    className={
                                      "min-w-0 flex-1 text-[13.5px] font-bold"
                                    }
                                    style={{
                                      color: isDone ? "#8A90A2" : "#1F2433",
                                    }}>
                                    {s.label}
                                  </span>
                                  {STEP_CTA[s.kind] && !isDone && (
                                    <button
                                      type={"button"}
                                      onClick={() => runStep(s.kind, key)}
                                      className={
                                        "h-[30px] flex-none rounded-[7px] border-[1.5px] border-[#DCD3FF] bg-white px-3 text-[12.5px] font-bold text-[#5B3FE0] hover:border-[#6B4EFF] hover:bg-[#6B4EFF] hover:text-white"
                                      }>
                                      {STEP_CTA[s.kind]}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className={"mb-[18px] mt-[22px] flex items-start gap-3"}>
          <span
            className={
              "inline-flex h-10 w-10 flex-none items-center justify-center rounded-full text-[13px] font-bold text-white"
            }
            style={{ background: avatarColor(sender?.email || "") }}>
            {initialsOf(displayName(sender))}
          </span>
          <div className={"min-w-0 flex-1"}>
            <div className={"flex flex-wrap items-baseline gap-2"}>
              <span className={"text-[15px] font-bold text-[#1F2433]"}>
                {displayName(sender)}
              </span>
              <span className={"text-[13px] text-[#8A90A2]"}>
                {sender?.email}
              </span>
            </div>
            <div className={"mt-[2px] truncate text-[13px] text-[#8A90A2]"}>
              to{" "}
              {(email.to || []).map((t) => t.name || t.email).join(", ") || "—"}
              {email.cc?.length
                ? ` · cc ${email.cc.map((c) => c.name || c.email).join(", ")}`
                : ""}
            </div>
          </div>
          <span
            className={
              "flex-none whitespace-nowrap text-[12.5px] text-[#8A90A2]"
            }>
            {fullDate(email.date)}
          </span>
        </div>
        <div
          ref={bodyRef}
          className={
            "mail-body max-w-[760px] overflow-x-auto text-[14.5px] leading-[1.7] text-[#2B3345] [overflow-wrap:anywhere] [&_img]:max-w-full"
          }
          dangerouslySetInnerHTML={{ __html: email.body || "" }}
        />
        {files.length > 0 && (
          <div className={"mt-6 border-t border-[#F0F1F4] pt-[18px]"}>
            <div className={"mb-[10px] text-[12.5px] font-bold text-[#8A90A2]"}>
              {files.length} {files.length === 1 ? "attachment" : "attachments"}
            </div>
            <div className={"flex flex-wrap gap-[10px]"}>
              {files.map((f) => {
                const ext = (f.filename.split(".").pop() || "file")
                  .slice(0, 4)
                  .toUpperCase();
                const ai = labelFor(f.filename);
                return (
                  <div
                    key={f.id || f.filename}
                    className={
                      "flex w-[240px] flex-col rounded-[10px] border border-[#E5E7EB] bg-white hover:border-[#6B4EFF]"
                    }>
                    <button
                      type={"button"}
                      onClick={() => p.onOpenAttachment(f)}
                      className={
                        "inline-flex w-full items-center gap-[10px] rounded-t-[10px] py-[9px] pl-[9px] pr-3 text-left hover:bg-[#FAF9FF]"
                      }>
                      <span
                        className={
                          "inline-flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[8px] bg-[#FDECEC] text-[10px] font-extrabold text-[#D64545]"
                        }>
                        {ext}
                      </span>
                      <span className={"flex min-w-0 flex-1 flex-col gap-px"}>
                        <span
                          className={
                            "truncate text-[13px] font-bold text-[#1F2433]"
                          }>
                          {f.filename}
                        </span>
                        <span
                          className={
                            "flex items-center gap-[6px] whitespace-nowrap text-[12px] text-[#9097A8]"
                          }>
                          {Math.max(1, Math.round((f.size || 0) / 1024))} KB
                          {ai && (
                            <span
                              className={
                                "inline-flex items-center gap-[3px] truncate font-bold text-[#6B4EFF]"
                              }>
                              <SparkleIcon size={10} />
                              {ai}
                            </span>
                          )}
                        </span>
                      </span>
                      <LuDownload
                        size={15}
                        className={"flex-none text-[#9097A8]"}
                      />
                    </button>
                    <button
                      type={"button"}
                      aria-label={`Save ${f.filename} to client files`}
                      onClick={() => p.onSaveAttachment(f)}
                      className={
                        "flex items-center gap-[6px] rounded-b-[10px] border-t border-[#F0F1F4] px-3 py-[6px] text-left text-[12px] font-bold text-[#6B4EFF] hover:bg-[#F4F2FF]"
                      }>
                      <LuFolderInput size={13} />
                      Save to client files
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* reply dock */}
      <div
        className={
          "flex-none border-t border-[#F0F1F4] bg-white px-[22px] pb-4 pt-3"
        }>
        {p.aiOn && replies.length > 0 && (
          <div className={"mb-[10px] flex flex-wrap items-center gap-[7px]"}>
            <span
              className={
                "mr-[2px] inline-flex items-center gap-[5px] text-[12px] font-bold text-[#8A80C0]"
              }>
              <SparkleIcon size={12} className={"text-[#7C5CFF]"} />
              Quick replies
            </span>
            {replies.map((r) => (
              <button
                type={"button"}
                key={r.label}
                onClick={() =>
                  p.onReply({ text: r.text, label: "Drafted with AI" })
                }
                className={
                  "inline-flex h-[30px] items-center whitespace-nowrap rounded-[15px] border border-[#E4DCFF] bg-[#F6F3FF] px-3 text-[12.5px] font-bold text-[#4A36B8] hover:border-[#6B4EFF] hover:bg-[#6B4EFF] hover:text-white"
                }>
                {r.label}
              </button>
            ))}
          </div>
        )}
        {p.aiOn && noReply && (
          <div
            className={
              "mb-[10px] flex items-center gap-[7px] text-[12.5px] text-[#8A90A2]"
            }>
            <SparkleIcon size={12} className={"text-[#A79BE0]"} />
            No reply needed — this looks like an automated email.
          </div>
        )}
        <div className={"flex items-center gap-[10px]"}>
          <div
            role={"button"}
            tabIndex={0}
            onClick={() => p.onReply()}
            onKeyDown={(e) => e.key === "Enter" && p.onReply()}
            className={
              "flex h-[42px] min-w-0 flex-1 cursor-text items-center gap-[10px] rounded-[9px] border-[1.5px] border-[#DCE2EA] pl-[14px] pr-[6px] text-[14px] text-[#9097A8] hover:border-[#6B4EFF]"
            }>
            <LuReply size={16} className={"flex-none"} />
            <span className={"min-w-0 flex-1 truncate"}>
              Reply to {displayName(p.outgoing ? email.to?.[0] : sender)}…
            </span>
            {p.aiOn && !noReply && (
              <button
                type={"button"}
                onClick={(e) => {
                  e.stopPropagation();
                  p.onReply(
                    replies[0]
                      ? { text: replies[0].text, label: "Drafted with AI" }
                      : { prompt: "Write a reply to this email" },
                  );
                }}
                className={
                  "inline-flex h-[30px] flex-none items-center gap-[6px] rounded-[7px] px-[11px] text-[12.5px] font-bold text-white hover:brightness-110"
                }
                style={{
                  background: "linear-gradient(135deg,#6B4EFF,#9152FF)",
                }}>
                <SparkleIcon size={13} />
                Draft reply
              </button>
            )}
          </div>
          <button
            type={"button"}
            onClick={p.onForward}
            className={
              "inline-flex h-[42px] items-center gap-[7px] rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-4 text-[13.5px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
            }>
            <LuForward size={15} />
            Forward
          </button>
        </div>
      </div>
    </section>
  );
};
